import React, { useEffect, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { callDigitalAssets } from '../utils/digitalAssetsClient';
import { useServiceFeePolicy } from '../hooks/useServiceFeePolicy';
import { DEFAULT_SERVICE_FEE, decimalToAtomic, formatUnits, percentageToBasisPoints } from '../utils/serviceFees';
import './PublishingFeePolicy.css';
const DEFAULT_FEES = { collectionCreationFeeEnabled: false, collectionCreationFeeLamports: '0', saleFeeEnabled: false, saleFeeBasisPoints: 0, treasuryWallet: '' };
const SERVICES = [
    ['studioMint', 'Creator Studio self-mints', 'Percentage fees use estimated Solana network cost plus account rent. Uploads are excluded.'],
    ['directPayment', 'Direct-payment unlocks', 'Added to the unlock price. Percentage fees use the payment token; fixed fees use SOL.'],
    ['donation', 'Donations', 'Added to the donation. The recipient receives the stated donation, before any token-provider transfer fees.'],
];
function toDraft(source) {
    const next = { ...DEFAULT_FEES, ...source };
    next.collectionCreationFeeSol = formatUnits(next.collectionCreationFeeLamports);
    SERVICES.forEach(([kind]) => {
        const rule = { ...DEFAULT_SERVICE_FEE, ...source[kind + 'Fee'] };
        next[kind + 'Fee'] = { ...rule, fixedAmountSol: formatUnits(rule.fixedLamports), percentage: String(rule.basisPoints / 100) };
    });
    return next;
}
export default function PublishingFeePolicy() {
    const wallet = useWallet();
    const { policy: remotePolicy, loading, error } = useServiceFeePolicy();
    const [policy, setPolicy] = useState(() => toDraft(DEFAULT_FEES));
    const [version, setVersion] = useState(0);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    useEffect(() => {
        // A save response may arrive before its Firestore snapshot. Keep the
        // acknowledged values until that snapshot catches up.
        if (remotePolicy && !dirty && (remotePolicy.version || 0) >= version) {
            setPolicy(toDraft(remotePolicy)); setVersion(remotePolicy.version || 0);
        }
    }, [remotePolicy, dirty, version]);
    const change = patch => { setDirty(true); setPolicy(previous => ({ ...previous, ...patch })); };
    const save = async event => {
        event.preventDefault();
        if (busy || loading || error) return;
        try {
            setBusy(true); setMessage('');
            const updated = { ...policy, collectionCreationFeeLamports: decimalToAtomic(policy.collectionCreationFeeSol) };
            SERVICES.forEach(([kind]) => {
                const rule = policy[kind + 'Fee'];
                updated[kind + 'Fee'] = { enabled: rule.enabled, mode: rule.mode,
                    fixedLamports: rule.mode === 'fixed' ? decimalToAtomic(rule.fixedAmountSol) : '0', basisPoints: rule.mode === 'percentage' ? percentageToBasisPoints(rule.percentage) : 0 };
            });
            await callDigitalAssets('saveFeePolicy', { policy: updated, expectedVersion: version }, wallet);
            setVersion(version + 1); setDirty(false); setMessage('Service fee policy saved.');
        } catch (failure) { setMessage(failure.message); }
        finally { setBusy(false); }
    };
    return <section className="admin-panel publishing-fee-policy">
        <h2>Service fees</h2>
        <p>Configure each service independently. Fixed fees accept decimal SOL amounts. Network, upload, and token-provider fees are separate.</p>
        {loading && <p role="status">Loading service fees...</p>}
        {error && <p role="alert">{error}</p>}
        {!loading && !error && version === 0 && <p>No service fees have been saved yet. Save these settings to finish setup.</p>}
        <form onSubmit={save}>
            <fieldset disabled={busy || loading || !!error} className="publishing-fee-policy__fields">
                <label>Treasury wallet (public address)
                    <input className="muulti-input" value={policy.treasuryWallet} onChange={event => change({ treasuryWallet: event.target.value })} />
                </label>
                <div className="publishing-fee-policy__grid">
                    <fieldset>
                        <legend>Collection creation</legend>
                        <label className="publishing-fee-policy__toggle"><input type="checkbox" checked={policy.collectionCreationFeeEnabled} onChange={event => change({ collectionCreationFeeEnabled: event.target.checked })} /> Enable collection creation fee</label>
                        <label>Collection fee (SOL)<input className="muulti-input" inputMode="decimal" value={policy.collectionCreationFeeSol} onChange={event => change({ collectionCreationFeeSol: event.target.value })} /></label>
                        <small>Fixed SOL amount, charged in the collection creation transaction.</small>
                    </fieldset>
                    <fieldset>
                        <legend>Paid NFT sales</legend>
                        <label className="publishing-fee-policy__toggle"><input type="checkbox" checked={policy.saleFeeEnabled} onChange={event => change({ saleFeeEnabled: event.target.checked })} /> Enable sale commission</label>
                        <label>Sale commission (basis points)<input className="muulti-input" type="number" min="0" max="10000" step="1" value={policy.saleFeeBasisPoints} onChange={event => change({ saleFeeBasisPoints: event.target.value })} /></label>
                        <small>250 basis points = 2.5%. Deducted from creator proceeds on paid NFT sales.</small>
                    </fieldset>
                    {SERVICES.map(([kind, title, help]) => {
                        const key = kind + 'Fee';
                        const rule = policy[key];
                        const update = patch => change({ [key]: { ...rule, ...patch } });
                        return <fieldset key={kind}>
                            <legend>{title}</legend>
                            <label className="publishing-fee-policy__toggle"><input type="checkbox" checked={rule.enabled} onChange={event => update({ enabled: event.target.checked })} /> Enable {title.toLowerCase()} fee</label>
                            <label>Fee type for {title.toLowerCase()}<select className="muulti-input" value={rule.mode} onChange={event => update({ mode: event.target.value })}>
                                <option value="fixed">Fixed amount (SOL)</option><option value="percentage">Percentage</option>
                            </select></label>
                            {rule.mode === 'fixed' ? <label>Fixed fee for {title.toLowerCase()} (SOL)<input className="muulti-input" inputMode="decimal" value={rule.fixedAmountSol} onChange={event => update({ fixedAmountSol: event.target.value })} /></label> :
                                <label>Percentage for {title.toLowerCase()} (%)<input className="muulti-input" inputMode="decimal" value={rule.percentage} onChange={event => update({ percentage: event.target.value })} /></label>}
                            <small>{help}</small>
                        </fieldset>;
                    })}
                </div>
                <button type="submit" className="muulti-button">{busy ? 'Saving...' : 'Save service fees'}</button>
            </fieldset>
        </form>
        {message && <p role="status">{message}</p>}
    </section>;
}
